import { useEffect, useState } from 'react';
import { adminApi, downloadBlob } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Download, Loader2, RefreshCcw, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

const statusTone = (statusCode) => {
  const value = Number(statusCode || 0);
  if (value >= 500) return 'bg-[#FDECEC] text-[#B42318]';
  if (value >= 400) return 'bg-[#FFF4E5] text-[#9A6700]';
  return 'bg-[#E8F5EC] text-[#2E7D32]';
};

export default function SecurityAuditPage() {
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    action: 'all',
    date_from: '',
    date_to: '',
  });

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        search: filters.search || undefined,
        action: filters.action === 'all' ? undefined : filters.action,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        page: 1,
        page_size: 100,
      };
      const response = await adminApi.getSecurityAuditLogs(params);
      setRows(response.data?.items || []);
      setSummary(response.data?.summary || null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load security audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const handleDownload = async () => {
    setExporting(true);
    try {
      const response = await adminApi.downloadAdminDatasetExcel('security-audit-logs', {
        search: filters.search || undefined,
        status: filters.action === 'all' ? undefined : filters.action,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
      });
      downloadBlob(response.data, 'security-audit-logs.xlsx');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to download security audit logs');
    } finally {
      setExporting(false);
    }
  };

  if (!hasPermission('all')) {
    return (
      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-8 text-center text-[#8B6F62]">
          You do not have permission to view security audit logs.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="security-audit-page">
      {exporting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35">
          <div className="rounded-2xl bg-white px-6 py-5 shadow-xl flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-[#C97A5E]" />
            <p className="text-[#2D2420] font-medium">Please wait. Report is being generated</p>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Security Audit Logs</h1>
          <p className="text-[#A8A8A8] mt-1">Review sensitive admin actions, failed requests, and recent high-risk activity.</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={fetchLogs} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
            Refresh
          </Button>
          <Button variant="outline" onClick={handleDownload} disabled={exporting}>
            <Download className="w-4 h-4 mr-2" />
            Download Excel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border-[#E5E5E5]"><CardContent className="p-4"><p className="text-sm text-[#A8A8A8]">Actions Last 24h</p><p className="text-2xl font-bold text-[#2D2420]">{summary?.last_24h_total ?? '-'}</p></CardContent></Card>
        <Card className="bg-white border-[#E5E5E5]"><CardContent className="p-4"><p className="text-sm text-[#A8A8A8]">Failures Last 24h</p><p className="text-2xl font-bold text-[#B42318]">{summary?.last_24h_failures ?? '-'}</p></CardContent></Card>
        <Card className="bg-white border-[#E5E5E5]"><CardContent className="p-4"><p className="text-sm text-[#A8A8A8]">High-Risk Actions Last 24h</p><p className="text-2xl font-bold text-[#9A6700]">{summary?.last_24h_high_risk ?? '-'}</p></CardContent></Card>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420] flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-[#C97A5E]" />
            Filter Logs
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Input
            placeholder="Search path, actor, IP..."
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
          />
          <Select value={filters.action} onValueChange={(value) => setFilters((prev) => ({ ...prev, action: value }))}>
            <SelectTrigger><SelectValue placeholder="Method" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Methods</SelectItem>
              <SelectItem value="POST">POST</SelectItem>
              <SelectItem value="PUT">PUT</SelectItem>
              <SelectItem value="PATCH">PATCH</SelectItem>
              <SelectItem value="DELETE">DELETE</SelectItem>
            </SelectContent>
          </Select>
          <Input type="date" value={filters.date_from} onChange={(e) => setFilters((prev) => ({ ...prev, date_from: e.target.value }))} />
          <Input type="date" value={filters.date_to} onChange={(e) => setFilters((prev) => ({ ...prev, date_to: e.target.value }))} />
          <Button onClick={fetchLogs} className="bg-[#C97A5E] hover:bg-[#B06850]">Apply Filters</Button>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">Recent Audit Trail</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-[#8B6F62] border-b border-[#F0E7E2]">
                <th className="py-3 pr-4 font-medium">Time</th>
                <th className="py-3 pr-4 font-medium">Method</th>
                <th className="py-3 pr-4 font-medium">Path</th>
                <th className="py-3 pr-4 font-medium">Status</th>
                <th className="py-3 pr-4 font-medium">Actor</th>
                <th className="py-3 pr-4 font-medium">IP</th>
                <th className="py-3 pr-4 font-medium">User Agent</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-[#F7F2EE] align-top">
                  <td className="py-3 pr-4 text-[#5C4033] whitespace-nowrap">{formatDateTime(row.created_at)}</td>
                  <td className="py-3 pr-4 text-[#5C4033]">{row.method}</td>
                  <td className="py-3 pr-4 text-[#2D2420]">
                    <div className="font-medium">{row.path}</div>
                    {row.query ? <div className="text-xs text-[#A8A8A8] break-all">{row.query}</div> : null}
                  </td>
                  <td className="py-3 pr-4"><Badge className={statusTone(row.status_code)}>{row.status_code}</Badge></td>
                  <td className="py-3 pr-4 text-[#5C4033]">
                    <div>{row.actor_name || '-'}</div>
                    <div className="text-xs text-[#A8A8A8]">{row.actor_role || '-'}</div>
                  </td>
                  <td className="py-3 pr-4 text-[#5C4033] whitespace-nowrap">{row.ip_address || '-'}</td>
                  <td className="py-3 pr-4 text-[#8B6F62] max-w-[280px] break-words">{row.user_agent || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && !rows.length ? <p className="py-6 text-center text-[#A8A8A8]">No audit log entries found for the selected filters.</p> : null}
        </CardContent>
      </Card>
    </div>
  );
}
